import { ModalBridgeContext } from '@services/ModalBridgeContext';
import { ref, Ref, unref, toRefs, computed, watch, toRef } from '@vue/composition-api';
import { useMutation, useQuery } from 'vue-query';
import { BerlinBffContext } from '@services/BerlinBff';
import {
  AtoneMemberValidatePostRequestSexDivisionEnum,
  GuestTypePostResponseMemberTypeEnum,
  GuestUpdateValidatePostRequestSexDivisionEnum,
} from '@netprotections/berlin-bff';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { useVuelidate } from '@vuelidate/core';
import useErrors from '@composables/useErrors';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import useAndAll from '@berlin-front/common/composables/useAndAll';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import { createContext, useContext } from '@berlin-front/common/composables/context';
import {
  getErrors,
  targetRequired,
  requiredQuiet,
  maxLength,
  regexp,
  corpRegex,
  fullAddress,
  email,
  zenkaku,
  kana,
  includes,
  validDay,
  REG_PASSWORD,
  REG_DATE_OF_BIRTH,
  REG_YEAR_OF_BIRTH,
  REG_PHONE_NUMBER_STYLE,
  REG_PHONE_NUMBER_STYLE_ERR_MSG,
  REG_ZIP_STYLE,
  REG_ZIP_STYLE_ERR_MSG,
  EXTENSION_MESSAGE_ADDRESS2_REQUIRED,
} from '@berlin-front/common/composables/validator';
import { ROUTE_NAME } from '@/router/route-name';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { MemberRequestContext } from '@views/member/MemberRequestContext';
import { caseCompleted, isMinor, ValueTypeOf } from '@berlin-front/common/utils';

type LinkProp = {
  name: ValueTypeOf<typeof ROUTE_NAME>;
  query?: Record<string, string | string[]>;
};

type UseMemberRegisterFactoryParam = {
  useLinks: () => Ref<
    | {
        prevPage?: LinkProp;
        nextPage: LinkProp;
      }
    | undefined
  >;
  contextModify?: () => void;
  skipCallApiTypePost?: boolean | undefined; // skip call API check member type
};

/**
 * 会員情報入力のcomposable
 *
 * 画面から使用するcomposableには非同期処理を書かない。
 * @returns
 */
export const useMemberRegisterFactory = (param: UseMemberRegisterFactoryParam) => () => {
  const { useLinks, contextModify, skipCallApiTypePost } = param;
  const links = useLinks();

  if (contextModify) {
    contextModify();
  }

  const { tokens, setGuestTypePostResult, guestTypePostResult } = useContext(ApiCacheContext);
  const {
    registerMemberFormState: state,
    prefectureName,
    parentalConsentCheck,
    termsConsentCheck,
    isEmailEditable,
    memberRegistrationRoute,
    zipF3,
    zipB4,
    address2,
    birthday,
    SEX_DIVISION,
    sexDivisionRender,
    MONTH_LIST,
    monthToString,
    normalize,
    userNo,
  } = useContext(MemberRequestContext);
  const bridge = useContext(ModalBridgeContext);

  // 初期化段階でどうしても値が決まらない場合、画面を表示する直前のこのタイミングで、2（女性）とします。
  state.sexDivision ||= '2';

  const { member, shop, zipAddress, napoleon } = useContext(BerlinBffContext);
  const router = useRouter();

  const usablePhoneNumberMessage = ref('');
  const memberType = computed(() => guestTypePostResult.value?.memberType);

  const MEMBER_TYPE = {
    NEW: GuestTypePostResponseMemberTypeEnum.NUMBER_0,
    NP_MEMBER: GuestTypePostResponseMemberTypeEnum.NUMBER_1,
    MEMBER: GuestTypePostResponseMemberTypeEnum.NUMBER_2,
    GUEST: GuestTypePostResponseMemberTypeEnum.NUMBER_3,
  } as const;

  /**
   * 利用規約の外部リンクURL
   */
  const SERVICE_URL = process.env.VUE_APP_ATONE_TERMS_OF_USE_URL;

  const computedFullAddress = computed(() =>
    [state.address2First || '', state.address2Second || '', state.address3 || ''].join(''),
  );
  // バリデーション
  const rules = computed(() => ({
    mailAddress: {
      targetRequired: targetRequired('メールアドレス'),
      maxLength: maxLength(256),
      email,
    },

    familyName: {
      targetRequired: targetRequired('姓'),
      maxLength: maxLength(50),
      corpRegex: corpRegex(),
      zenkaku: zenkaku('姓'),
    },

    givenName: {
      targetRequired: targetRequired('名'),
      maxLength: maxLength(50),
      zenkaku: zenkaku('名'),
      corpRegex: corpRegex(),
    },

    familyNameKana: {
      targetRequired: targetRequired('よみがな姓'),
      maxLength: maxLength(64),
      kana: kana('よみがな姓'),
    },

    givenNameKana: {
      targetRequired: targetRequired('よみがな名'),
      maxLength: maxLength(64),
      kana: kana('よみがな名'),
    },

    birthdayYear: {
      targetRequired: targetRequired('生年月日'),
      regexp: regexp(REG_YEAR_OF_BIRTH),
    },

    birthdayMonth: {
      targetRequired: targetRequired('生年月日'),
      regexp: includes(MONTH_LIST),
    },

    birthdayDay: {
      targetRequired: targetRequired('生年月日'),
      regexp: regexp(REG_DATE_OF_BIRTH),
      validDay: validDay(toRef(state, 'birthdayYear'), toRef(state, 'birthdayMonth')),
    },

    zip: {
      targetRequired: targetRequired('郵便番号'),
      regexp: regexp(REG_ZIP_STYLE, REG_ZIP_STYLE_ERR_MSG),
    },

    prefectureCode: {
      targetRequired: targetRequired('都道府県'),
    },

    address2First: {
      targetRequired: targetRequired('市区町村'),
      zenkaku: zenkaku('市区町村'),
      fullAddress: fullAddress(computedFullAddress),
    },

    address2Second: {
      address2SecondRequired: targetRequired('番地', EXTENSION_MESSAGE_ADDRESS2_REQUIRED),
      zenkaku: zenkaku('番地'),
      fullAddress: fullAddress(computedFullAddress),
    },

    address3: {
      zenkaku: zenkaku('建物名・部屋番号'),
      fullAddress: fullAddress(computedFullAddress),
    },

    phoneNumber: {
      targetRequired: targetRequired('電話番号'),
      maxLength: maxLength(11),
      regexp: regexp(REG_PHONE_NUMBER_STYLE, REG_PHONE_NUMBER_STYLE_ERR_MSG),
    },

    password: isEmailEditable.value
      ? {
          requiredQuiet,
          regexp: regexp(REG_PASSWORD),
        }
      : {
          requiredQuiet: () => true,
          regexp: () => true,
        },
  }));

  /**
   * 未成年者チェック表示フラグ
   */
  const isParentalConsentCheckRequired = computed(() => {
    if (isMinor(unref(birthday))) {
      return true;
    }
    return false;
  });

  watch(isParentalConsentCheckRequired, () => {
    // 未成年<->成年の切り替わりで、保護者同意チェックを外す。
    parentalConsentCheck.value = false;
  });

  /**
   * チェック項目のチェック入り確認フラグ
   */
  const isAllChecked = computed(
    // 未成年のときのみ保護者同意チェックを評価する。
    () => (!isParentalConsentCheckRequired.value || parentalConsentCheck.value) && termsConsentCheck.value,
  );

  /**
   * 利用可能な携帯電話取得API
   */
  const { isSuccess: usablePhoneNumberMessageSuccess } = useQuery(
    'atone.shopUsablePhoneNumberGet',
    () => shop.atoneShopControllerShopUsablePhoneNumberGet(bridge.pubkey),
    {
      onSuccess: ({ data }) => {
        usablePhoneNumberMessage.value = data.usablePhoneNumberMessage ?? '';
      },
    },
  );

  /**
   * 郵便番号から住所情報を取得・入力フォームに自動入力します。
   */
  const { refetch: refetchAddress, isLoading: isAddressLoading } = useQuery(
    'atone.zipAddressGet',
    () => zipAddress.atoneZipAddressControllerZipAddressGet(unref(zipF3), unref(zipB4)),
    {
      enabled: computed(() => /^\d{7}$/.test(state.zip)),
      onSuccess: ({ data }) => {
        Object.assign(state, {
          prefectureCode: data.prefectureCode,
          address2First: data.address2,
        });
      },
    },
  );
  watch(prefectureName, (value, oldValue) => {
    if (!oldValue && typeof value === 'string' && value.length > 0) {
      if (state.address2Second.startsWith(value)) {
        state.address2Second = state.address2Second.slice(value.length).trim();
      }
      if (state.address2Second.startsWith(state.address2First)) {
        state.address2Second = state.address2Second.slice(state.address2First.length).trim();
        normalize();
      } else {
        // 市区町村とプリセットパラメータが部分一致しない場合は市区町村建物名とも殺す。
        state.address2Second = '';
        state.address3 = '';
      }
    }
  });

  /**
   * 会員種別特定。新規登録か仮会員昇格かを判断するために呼び出す。
   */
  const {
    isSuccess: typePostSuccess,
    // isError: typePostError,
    mutate,
  } = useMutation(
    'napoleon.guestType',
    () =>
      napoleon.guestControllerTypePost(tokens.value?.requestToken ?? '', {
        mailAddress: state.mailAddress,
        phoneNumber: state.phoneNumber,
      }),
    {
      onSuccess: ({ data }) => {
        setGuestTypePostResult(data);
      },
    },
  );
  /**
   * 会員登録バリデーションチェック。新規会員登録の場合に呼び出す
   */
  const {
    isSuccess: validateMemberIsSuccess,
    isError: validateMemberIsError,
    isLoading: validateMemberIsLoading,
    error: validateMemberError,
    mutate: validateMember,
  } = useMutation('validate-member', () =>
    member.atoneMemberControllerMemberValidatePost(tokens.value?.requestToken || '', {
      memberRegistrationRoute: unref(memberRegistrationRoute),
      mailAddress: state.mailAddress,
      familyName: state.familyName,
      familyNameKana: state.familyNameKana,
      givenName: state.givenName,
      givenNameKana: state.givenNameKana,
      sexDivision: state.sexDivision as unknown as AtoneMemberValidatePostRequestSexDivisionEnum,
      birthday: unref(birthday),
      zipF3: unref(zipF3),
      zipB4: unref(zipB4),
      prefectureCode: state.prefectureCode,
      address2: unref(address2),
      address3: state.address3,
      phoneNumber: state.phoneNumber,
      password: state.password,
      passwordConfirm: state.password,
    }),
  );

  /**
   * 仮会員昇格バリデーションチェック。仮会員が会員登録した場合に呼び出す
   */
  const {
    isSuccess: validateGuestUpdateIsSuccess,
    isError: validateGuestUpdateIsError,
    isLoading: validateGuestUpdateLoading,
    mutate: validateGuestUpdate,
    // error: validateGuestUpdateError,
  } = useMutation('napoleon.guestUpdate', () =>
    napoleon.guestControllerUpdateValidatePost(tokens.value?.requestToken ?? '', bridge.pubkey ?? '', {
      mailAddress: state.mailAddress,
      familyName: state.familyName,
      familyNameKana: state.familyNameKana,
      givenName: state.givenName,
      givenNameKana: state.givenNameKana,
      sexDivision: state.sexDivision as GuestUpdateValidatePostRequestSexDivisionEnum,
      birthday: unref(birthday),
      zipF3: unref(zipF3),
      zipB4: unref(zipB4),
      prefectureCode: state.prefectureCode,
      address2: unref(address2),
      address3: state.address3,
      phoneNumber: state.phoneNumber,
      password: state.password,
      userNo,
    }),
  );

  // 会員種別に応じてバリデーションを実行
  // 会員種別の判定前は何もしない
  watch(typePostSuccess, () => {
    if (typePostSuccess) {
      switch (memberType.value) {
        case undefined:
          break;
        case MEMBER_TYPE.GUEST:
          validateGuestUpdate();
          break;
        case MEMBER_TYPE.MEMBER:
        case MEMBER_TYPE.NEW:
        case MEMBER_TYPE.NP_MEMBER:
          validateMember();
          break;
        default:
          caseCompleted(memberType.value);
          break;
      }
    }
  });

  // 状況
  const isSuccess = useOrAll(validateGuestUpdateIsSuccess, validateMemberIsSuccess);
  const isError = useOrAll(validateGuestUpdateIsError, validateMemberIsError);
  const isLoading = useOrAll(validateMemberIsLoading, validateGuestUpdateLoading);

  // 成功、または失敗になったらisEnded
  const isEnded = useOrAll(isSuccess, isError);
  // useMutationの状況に合わせてプログレスを進める
  const {
    progress,
    completed: validateIsCompleted,
    isLoading: progressIsLoading,
  } = toRefs(useFakeProgress(2000, isLoading, isEnded));

  /**
   * エラーハンドリング
   */
  const {
    errors: externalErrors, // Record<FieldName, string[]> フィールド名別にエラーをまとめている。
    clearAll: clearErrorMessages, // 全てのエラーを解除する。APIを実行する前に叩くと良い。
  } = useErrors({
    // APIのエラーに含まれるparamによって、エラーメッセージをstateのfieldに割り当てる。
    // fieldMapper: {},
    fieldMapper: {
      mailAddress: 'mailAddress',
      familyName: 'familyName',
      familyNameKana: 'familyNameKana',
      givenName: 'givenName',
      givenNameKana: 'givenNameKana',
      sexDivision: 'sexDivision',
      prefectureCode: 'prefectureCode',
      address3: 'address3',
      phoneNumber: 'phoneNumber',
      password: 'password',
    },
    // 相関関係にあるフィールドを定義。例えばパスワードとパスワード確認用みたいなものだが、これのサーババリデーション版。
    correlations: [
      ['zip', 'prefectureCode', 'address2First', 'address2Second', 'address3'], // 相関設定。 'field1'が更新されると'field2', 'field3'のエラーも更新される。逆も然り。
    ],
    // mapperに定義されてないエラーを、ここに設定した名称のフィールドに一括で割り当てる。
    // フィールドにバインドできないタイプのエラーメッセージを、画面上部のフラッシュメッセージに設定するために使用する。
    defaultFieldName: 'flashMessage',
    // statesをセットすると、フィールド更新を検出してエラーを解除する機能が有効になる。
    state,
  });
  const v$ = useVuelidate(rules, state, { $externalResults: externalErrors });
  const errors = getErrors(v$);

  // アラートメッセージ;
  const alertMessages = computed(() => unref(externalErrors)?.flashMessage ?? []);

  /**
   * 「確認画面へ進む」ボタンの活性化
   */
  const confirmButtonDisabled = computed(() => !isAllChecked.value || v$.value.$error);

  /**
   * birthdayに関するrequiredエラーのみをまとめたもの。
   */
  const birthdayInputRequiredErrors = computed(() =>
    [
      ...errors('birthdayYear').$errors.filter((e) => e.$validator === 'targetRequired'),
      ...errors('birthdayMonth').$errors.filter((e) => e.$validator === 'targetRequired'),
      ...errors('birthdayDay').$errors.filter((e) => e.$validator === 'targetRequired'),
    ].map((v) => unref(v.$message)),
  );

  /**
   * 生年月日のrequiredエラー。
   *
   * 表示時には先頭のエラーが優先される。
   */
  const birthdayInputErrors = computed(() => [
    ...birthdayInputRequiredErrors.value,
    ...errors('birthdayYear').$messages,
    ...errors('birthdayMonth').$messages,
    ...errors('birthdayDay').$messages,
  ]);

  /**
   * 「確認画面へ進む」ボタンクリックイベント
   */
  const confirmClick = async () => {
    if (!isLoading.value && !confirmButtonDisabled.value && (await v$.value.$validate())) {
      if (skipCallApiTypePost) {
        // 会員種別特定API のコールをスキップして、atone/member/validateを読む
        validateMember();
      } else {
        // 会員種別特定API読んで、memberTypeによって適当な処理をする
        mutate();
      }
    }
  };

  watch([useAndAll(isSuccess, validateIsCompleted), links] as const, ([completed, linksValue]) => {
    if (completed && linksValue) {
      // 確認画面に遷移する前、入力内容を保存する
      // エラーメッセージクリア
      clearErrorMessages();
      void router.replace(linksValue.nextPage);
    }
  });

  /**
   * 「戻る」ボタンクリックイベント
   */
  const backClick = () => {
    if (links.value?.prevPage) {
      void router.replace(links.value.prevPage);
    }
  };

  /**
   * 「住所を検索」ボタンクリックイベント
   */
  const addressSearchClick = () => {
    if (!v$.value.zip.$error && refetchAddress.value && !isAddressLoading.value) {
      void refetchAddress.value();
    }
  };

  return {
    state,
    prefectureName,
    isEmailEditable,
    termsConsentCheck,
    // 未成年者チェック表示フラグ
    isParentalConsentCheckRequired,
    parentalConsentCheck,
    birthdayInputErrors,

    confirmButtonDisabled,

    // validate API
    validateMember,
    validateMemberIsSuccess,
    validateMemberIsError,
    validateMemberIsLoading,
    validateMemberError,
    progress,
    progressIsLoading,
    // validateIsCompleted,

    // 利用可能な電話番号 API
    usablePhoneNumberMessageSuccess,
    usablePhoneNumberMessage,

    // 表示項目
    SEX_DIVISION,
    sexDivisionRender,
    MONTH_LIST,
    monthToString,

    SERVICE_URL,

    // イベントハンドラー
    backClick,
    confirmClick,
    addressSearchClick,
    normalize,

    // エラーハンドラー
    alertMessages,
    errors,
    v$,
  };
};

export const MemberRegisterContext = createContext({
  useMemberRegister: useMemberRegisterFactory({ useLinks: () => ref({ nextPage: { name: ROUTE_NAME.HOME } }) }),
});
